import { parse as parseChrono } from "chrono-node"
import { add, addDays, addWeeks, format, startOfDay } from "date-fns"

export interface DueDateOption {
	label: string
	date: Date
}

type Unit = "hours" | "days" | "weeks" | "months"

const UNITS: Array<{ unit: Unit; singular: string; aliases: RegExp }> = [
	{ unit: "hours", singular: "hour", aliases: /^(h|hrs?|hours?)$/ },
	{ unit: "days", singular: "day", aliases: /^(d|days?)$/ },
	{ unit: "weeks", singular: "week", aliases: /^(w|wks?|weeks?)$/ },
	{ unit: "months", singular: "month", aliases: /^(mo|mos|months?)$/ },
]

export function getDueDatePresets(now = new Date()): DueDateOption[] {
	return [
		{ label: "Tomorrow", date: addDays(now, 1) },
		{ label: "In one week", date: addWeeks(now, 1) },
	]
}

function relativeOptions(query: string, now: Date): DueDateOption[] | null {
	const match = /^(?:in\s+)?(\d{1,3})\s*([a-z]*)$/.exec(query)
	if (!match) return null
	const amount = Number(match[1])
	const unitText = match[2] ?? ""
	const exact = UNITS.find(({ aliases }) => aliases.test(unitText))
	const units = exact
		? [exact]
		: UNITS.filter(({ unit }) =>
				// A bare number is a count of days, weeks or months, not hours.
				unitText ? unit.startsWith(unitText) : unit !== "hours",
			)
	return units.map(({ unit, singular }) => ({
		label: `In ${amount} ${amount === 1 ? singular : unit}`,
		date: add(now, { [unit]: amount }),
	}))
}

export function parseDueDateQuery(
	query: string,
	now = new Date(),
): DueDateOption[] {
	const trimmed = query.trim().toLowerCase()
	if (!trimmed) return []

	const options =
		relativeOptions(trimmed, now) ??
		parseChrono(trimmed, now, { forwardDate: true }).map((result) => ({
			label: result.text.charAt(0).toUpperCase() + result.text.slice(1),
			date: result.start.date(),
		}))

	const today = startOfDay(now)
	const seen = new Set<string>()
	return options.filter(({ date }) => {
		const day = toDueDate(date)
		if (date < today || seen.has(day)) return false
		seen.add(day)
		return true
	})
}

/** Linear stores a due date as a bare calendar day. */
export function toDueDate(date: Date) {
	return format(date, "yyyy-MM-dd")
}

/** "Fri, 16 Oct", the hint beside each option in Linear's menu. */
export function formatDueDateHint(date: Date) {
	return format(date, "EEE, d MMM")
}
